// Chart and Analysis Types
export interface TrendLine {
  slope: number;
  intercept: number;
  // FS-579: set when there is no rate to report at all — the filter period
  // has fewer than two processed days above the high-speed threshold, which
  // is what a device shows for a day or two after a change. slope is 0 there,
  // which reads as "this filter is not loading" — so show nothing instead.
  // Absent on any response predating FS-579, where the rate was always meant.
  rate_unavailable?: boolean;
  reason?: string;
}

export interface Targets {
  notice: number;
  change: number;
}

export interface ReturnDeltaTransition {
  delta: number;
  timestamp: string;
}

export interface ReturnDeltas {
  transitions: ReturnDeltaTransition[];
}

/** Server-computed explanation for an empty chart series.
 *
 *  Shared by every surface gated on the pipeline's fan-separation predicate:
 *  both transition-delta charts (FS-464) and the heat/cool runtime chart
 *  (FS-477). Each carries its own block — they share the gate, not the outcome.
 *
 *  `reason` is only meaningful when `available` is false:
 *   - `no_fan_off_state`     two or more clusters, but spread below the gate —
 *                            commonly a continuously running blower
 *   - `insufficient_clusters` a single pressure band all day
 *   - `no_data`              no processed days in range
 *   - `stale_calculation`    the row was computed by a superseded version of the
 *                            analysis and would produce a value if recomputed.
 *                            Named for the row rather than the mechanism (a
 *                            since-removed 10 Pa magnitude floor), because a
 *                            code naming a filter that no longer exists tells
 *                            the next reader nothing. Provable only because the
 *                            paired metric stored a value for the same day
 *   - `unavailable`          suppressed for a reason the server cannot name
 *                            (quarantine state is not persisted) — must render
 *                            neutrally rather than guess
 */
export type SeriesUnavailableReason =
  | 'no_fan_off_state'
  | 'insufficient_clusters'
  | 'no_data'
  | 'stale_calculation'
  | 'unavailable';

export interface SeriesStatus {
  available: boolean;
  reason: SeriesUnavailableReason | null;
  days_evaluated: number;
  days_with_value: number;
  observed_separation_pa: number | null;
  required_separation_pa: number;
}

export interface CycleStatsMode {
  mins: number;
  lag?: number;
  range?: number;
  cycles?: number;
  max_temp?: number;
  min_temp?: number;
  pressure?: number;
}

export interface CycleStats {
  cool?: CycleStatsMode;
  heat?: CycleStatsMode;
  cont?: CycleStatsMode;
  unknown?: CycleStatsMode;
}

export interface CycleModelOption {
  id: string;
  label: string;
  /** FS-729: the device's source analysis (the production-health owner). */
  source_analysis_key: string;
  source_analysis_name?: string;
}

export interface CycleModelResults {
  model: CycleModelOption;
  dates: string[];
  cycle_stats: (CycleStats | null)[];
  days_available: number;
}

/** Plan 81: one tracked fan-speed mode on one day (multi-mode analyses). */
export interface ModeDailyEntry {
  mode_id: string;
  rank: number;
  /** Median corrected pressure when the mode ran that day. */
  median_corrected_pa?: number;
  /** Off band only: median raw pressure. */
  median_raw_pa?: number;
  /** Ghost modes only: predicted position (latent resistance x mode factor). */
  predicted_corrected_pa?: number;
  minutes: number;
  confidence: number;
  /** False = ghost: tracked by the model but not observed that day.
   *  Absent on rows written before ghost support = observed. */
  observed?: boolean;
  /** Mode lifecycle: 'active' | 'stale' | 'dormant'. */
  status?: string;
}

export interface ChartData {
  reference_sources?: import('./referenceData').ReferenceSource[] | null;
  reference_error?: string;
  dates: string[];
  pressures: number[];
  runtimes: number[];
  cumulative_runtimes: number[];
  m_temperatures: number[];
  m_humidities: number[];
  /** FS-621: return-side daily minimum and maximum, parallel to `dates`, so the
   *  chart can show the spread the daily average hides. Absent when the series
   *  has no values at all, matching how the averages behave. NOT the same as
   *  `temperatures` below, which is the OUTSIDE weather high/low. */
  min_temperatures?: (number | null)[];
  max_temperatures?: (number | null)[];
  min_humidities?: (number | null)[];
  max_humidities?: (number | null)[];
  trend_line: TrendLine;
  runtime_trend_line: TrendLine;
  targets: Targets;
  temperatures?: {
    max: number[];
    min: number[];
  };
  return_deltas?: ReturnDeltas | (number | null)[];
  /** FS-464: why the return-delta series is empty, when it is. Computed
   *  server-side — the client only sees nulls and cannot tell a system with no
   *  fan-off state apart from a suppressed day. Absent on older payloads. */
  return_deltas_status?: SeriesStatus;
  fan_deltas?: (number | null)[];
  /** Per date, true when the blower never turned off. Absent on older payloads. */
  fan_never_off?: (boolean | null)[];
  /** FS-464: companion to return_deltas_status. Separate on purpose — the two
   *  share the pipeline's separation gate but not their outcomes, so one block
   *  would misreport coverage for whichever chart it was not built from. */
  fan_deltas_status?: SeriesStatus;
  cycle_stats?: (CycleStats | null)[];
  cycle_model_options?: CycleModelOption[];
  /** FS-477: why the heat/cool runtime chart is empty, when it is. Same gate as
   *  the deltas above, still its own block — 4 day-rows in 30 days carry cycle
   *  stats with neither delta, and 51 clear the gate but classify no cycle. */
  cycle_stats_status?: SeriesStatus;
  // FS-748: the daily airflow chart is gone and NOTHING in the client reads
  // estimated_cfm, cfm_calibrated, cfm_calibrated_by_day or the cfm_source* /
  // cfm_confidence / cfm_size_verified fields below any more. fetch_analysis
  // still sends them; they stay here, optional and unused, until Plan 127
  // removes them from the response. Airflow comes from /system-airflow
  // (SystemAirflowClip.calibration) instead.
  /** Plan 33: estimated airflow (CFM) per day, parallel to `dates`.
   *  SHIP MODE — uncalibrated: the filter_catalog curve is known-wrong, so
   *  these are RELATIVE-TREND ONLY, not absolute CFM. See `cfm_calibrated`. */
  estimated_cfm?: (number | null)[];
  /** Plan 81: per-day max-speed observation kind for multi-mode analyses
   *  ('observed' | 'imputed_from_band' | 'provisional_single_mode' |
   *  'carried_forward' | 'below_threshold' | 'none'), parallel to `dates`.
   *  Null for standard_daily_v1 (rendered as observed). Drives observed=solid /
   *  imputed=hollow point styling. */
  observations?: (string | null)[];
  /** Plan 81: per-day dp_max confidence [0,1], parallel to `dates`. */
  confidences?: (number | null)[];
  /** Plan 81: per-day dp_max sigma (Pa), parallel to `dates`. Only present
   *  for multi-mode analyses. */
  sigmas?: (number | null)[];
  /** Per-day dynamic calibration (Pa) applied to the analysis values:
   *  chained anchor (calibration_used) for standard, tracker off-band offset
   *  for multi-mode. The scatter plot applies only the static offset, so
   *  divergence here explains analysis-vs-scatter disagreements. */
  calibrations?: (number | null)[];
  /** FS-493: per-day observed fan-off band level (Pa, chart coordinates —
   *  values near 0), parallel to `dates`. Null = no off band observed that
   *  day; a day missing entirely still means "device not reporting". Absent
   *  on older cached payloads. */
  off_levels?: (number | null)[];
  /** Raw off level (Pa) used for each day's multi-mode zero correction,
   *  parallel to `dates` including held days; null when the day has no zero. */
  zero_pa?: (number | null)[];
  /** Plan 81: per-day per-mode detail for multi-mode analyses, parallel to
   *  `dates`. Includes observed bands (median_corrected_pa) AND ghost bands —
   *  modes the tracker still carries but did not see that day, projected to
   *  their predicted position (predicted_corrected_pa). Null per day for
   *  standard analyses / missing days. */
  mode_daily?: (ModeDailyEntry[] | null)[];
  /** True once the catalog curve is rebuilt from MAEDbS (Plan 33 Phase 1).
   *  While false, the UI must not present absolute CFM or capacity zones. */
  cfm_calibrated?: boolean;
  /** FS-724: per day, parallel to `dates` / `estimated_cfm` — true or false
   *  for a day with an estimate (as the nightly wrote it that day), null for
   *  a day with none. Since FS-724 `cfm_calibrated` is true only when every
   *  plotted day is; this says which days are in a mixed window. Absent from
   *  responses that predate it. */
  cfm_calibrated_by_day?: (boolean | null)[];
  /** Plan 33/109 source tier of the matched filter_catalog row: 'maedb' |
   *  'filter_label' | 'certified_scaled' (certified) | 'manufacturer' |
   *  'manual' | 'extrapolated' | 'estimated' | 'randy'. 'manufacturer'
   *  ranks with 'manual' in the matcher and never calibrates. Lets the chart
   *  label the tier beyond the binary cfm_calibrated flag. */
  cfm_source?: string | null;
  /** The matched row's source_meta. For 'certified_scaled' (FS-686) it names
   *  the certified row the curve was scaled from: base_filter_id,
   *  base_nominal, area_ratio, line_ids, line_numbers. */
  cfm_source_meta?: Record<string, unknown> | null;
  /** Pretty "brand model size" for extrapolation donor display. */
  cfm_source_donor?: string | null;
  /** FS-608: which filter the three fields above describe.
   *  'device'     — the devices row's cached match: no filter period selected,
   *                 or the current one, where that cache IS this period's
   *                 filter.
   *  'period'     — the selected previous period's own filter, resolved to a
   *                 catalog row at read time.
   *  'unresolved' — a previous period whose filter matches no catalog row (or
   *                 was never recorded). The daily estimates still came from
   *                 whatever curve the nightly used then; we cannot name it.
   *                 NOT the same as "no curve matched", and must not be
   *                 rendered as it. Absent on every response before the
   *                 fetch_analysis change ships — treat that as 'device'. */
  cfm_source_scope?: 'device' | 'period' | 'unresolved';
  /** FS-648: the nightly's ranking of the match — 'high' only for a
   *  certified source, size-verified, with the product resolved; else 'low'.
   *  From devices.filter_catalog_confidence on 'device' scope; null on
   *  'period' (a read-time family match is not the nightly's ranking) and
   *  'unresolved', and absent on older responses. Unknown, never guessed. */
  cfm_confidence?: 'high' | 'low' | null;
  /** FS-648: whether the match's nominal size was verified against the
   *  filter record. devices.filter_catalog_size_verified on 'device' scope;
   *  true on 'period' (period attribution requires the same nominal size);
   *  null on 'unresolved', and absent on older responses. */
  cfm_size_verified?: boolean | null;
}

/** FS-608: the filter period the analysis response describes. Present only
 *  when the request named one; absent means the devices row, as before. */
export interface AnalysisFilterPeriod {
  epoch_id: string;
  /** The period's own filter-change date. Null before the first recorded
   *  change, where no filter age can honestly be claimed. */
  effective_date: string | null;
  /** True for the newest recorded period — the one the devices row describes,
   *  where install_date / filter_start_date are still the right anchors. */
  is_current: boolean;
}

/** FS-608: the filter period the analysis response describes. Present only
 *  when the request named one; absent means the devices row, as before. */
export interface AnalysisFilterPeriod {
  epoch_id: string;
  /** The period's own filter-change date. Null before the first recorded
   *  change, where no filter age can honestly be claimed. */
  effective_date: string | null;
  /** True for the newest recorded period — the one the devices row describes,
   *  where install_date / filter_start_date are still the right anchors. */
  is_current: boolean;
}

/** FS-607 round 2: fetch_analysis.resolve_filter_anchor's answer.
 *
 *  `filter_change` — a recorded change (the app's filter flow, or one of
 *    Randy's notes). This date IS the filter's age.
 *  `install` — no recorded change and the filter was new when the Clip went
 *    in, so the installation is the filter's start.
 *  `measuring` — the Clip was put on a filter that was ALREADY IN SERVICE.
 *    Its age is unknown; the date is when measuring began.
 *  Both fields null when the device has nothing to anchor on. */
export interface AnalysisFilterAnchor {
  date: string | null;
  kind: 'filter_change' | 'install' | 'measuring' | null;
}

export interface AnalysisResults {
  centroids: number[];
  change_date: string;
  chart_data: ChartData;
  current_pressure: number;
  days_to_change: number;
  days_to_notice: number;
  notice_date: string;
  total_runtime: number;
  total_average_daily_runtime: number;
  avg_daily_runtime_30d?: number;
  avg_daily_runtime_30d_days?: number;
  airflow_percentage?: number | null;
  filter_info?: {
    thickness?: string | number;
    rating?: string;
    manufacturer?: string;
    length?: number;
    width?: number;
    model?: string;
    /** FS-594: filter_catalog row this filter matched, for the deep link to
     *  its performance data. Absent when nothing matched — and absent from
     *  every response until the fetch_analysis change ships, so the UI must
     *  treat it as optional. */
    filter_catalog_id?: number | null;
  } | null;
  install_date?: string | null;
  /** FS-607: start of the CURRENT filter's period. fetch_analysis has always
   *  returned it; the client only read install_date, so any device whose
   *  filter changed after installation was reported with the installation's
   *  age. Null after every filter change until the nightly re-establishes the
   *  baseline, and on a device that has never had one — so a caller must not
   *  call the count a filter change without checking this field. */
  filter_start_date?: string | null;
  /** FS-608: the selected filter period, when one was selected. Its
   *  effective_date is the only honest anchor for a previous period — the two
   *  fields above describe the CURRENT filter whichever period is showing. */
  filter_period?: AnalysisFilterPeriod | null;
  /** FS-607 round 2: the anchor the SERVER resolved for the current filter —
   *  the date the day count runs from, and what that date is. The page used
   *  to work this out from install_date / filter_start_date and disagreed
   *  with the phone card by months on the six devices whose baseline predates
   *  their own install. Optional: a response from a Lambda predating it has
   *  no field, and the old coalesce is then what its dates mean. */
  filter_anchor?: AnalysisFilterAnchor | null;
  /** Plan 127 Phase 3: the stored airflow record for the selected filter
   *  period, or null when there is none. Absent from a Lambda before it. */
  period_airflow?: PeriodAirflowRecord | null;
  status?: string;
  // Plan 80: analysis identity + the production/analysis health split.
  analysis_key?: string;
  selected_analysis?: AvailableAnalysis | null;
  available_analyses?: AvailableAnalysis[];
  production_filter_health?: ProductionFilterHealth;
  /** FS-1131: every input behind the current filter's Filter Life. Null for
   *  a previous period or a failed read; absent from a Lambda before it. */
  filter_life_calculation?: FilterLifeCalculation | null;
}

/** FS-1131: one day of the analysis that writes filter life. */
export interface FilterLifeDay {
  date: string;
  /** The day's top pressure: the value the nightly read filter life from. */
  pressure_pa: number | null;
  /** Minutes measured at the day's top pressure, when recorded. */
  top_minutes: number | null;
  /** The percentage stored for the day. */
  filter_life_pct: number;
  /** The formula over the start value and limit in force now. It differs
   *  from filter_life_pct when either has changed since the day ran. */
  computed_pct: number | null;
}

/** FS-1131: fetch_analysis.get_filter_life_calculation. */
export interface FilterLifeCalculation {
  /** The analysis that writes filter life for this device. */
  analysis_key: string | null;
  start_date: string | null;
  start_pa: number | null;
  /** Minutes measured at the start pressure on the start day, when recorded. */
  start_minutes: number | null;
  /** devices.filter_baseline_source: measured, measured_used, assumed… */
  start_source: string | null;
  end_of_life_pa: number;
  /** False when the limit is the fleet default. */
  end_of_life_on_device: boolean;
  /** The device's own replacement-alert pressure, when one is set. */
  notify_pa: number | null;
  /** Without notify_pa, the alert fires at this percentage of life left. */
  notice_pct: number;
  /** The pressure at which the replacement alert fires. */
  notice_pa: number;
  /** Minimum measured minutes at the top level for a day to set filter life. */
  min_cluster_minutes: number;
  latest: FilterLifeDay | null;
  /** The first day at this filter's lowest value: the day that set the
   *  floor the app shows. */
  lowest: FilterLifeDay | null;
}

// Plan 80: flexible daily analysis runs. A device may expose more than one
// named analysis; the analysis page selects/pins by (device, analysis_key).
export interface AvailableAnalysis {
  analysis_key: string;
  display_name: string;
  description?: string;
  is_default: boolean;
  side_effects: string[];          // enabled side-effect keys for this device
  owns_production_health: boolean; // true when it drives app-facing filter health
}

export interface ProductionFilterHealth {
  airflow_percentage: number | null;
}

// Multi-device comparison (Plan 31)
export type CompareOrientation = 'vertical' | 'horizontal' | 'overlaid';

export interface PinnedAnalysis {
  device: Device;
  results: AnalysisResults;
  color: string;
  // Plan 80: pins are keyed by (device.id, analysisKey) so the same device can
  // be pinned twice under two analyses. analysisLabel is the display name.
  analysisKey: string;
  analysisLabel: string;
  // FS-723: the last processed day (YYYY-MM-DD) of the window this analysis
  // was run against, when known. A range ending on it stopped there only
  // because the daily analysis has not gone further, so the raw scatter may
  // run on to today.
  latestProcessedDate?: string;
}

// Overlay-mode series passed to chart components. When provided (length > 1),
// the chart renders one trace per series on shared axes. Single-series or no
// series = existing per-device render.
export interface ChartSeries {
  id: string | number;
  label: string;
  color: string;
  data: ChartData;
}

// Analysis Request Types
export interface AnalysisRequest {
  use_cached: boolean;
  only_cached: boolean;
  device_name: string;
  agg: 'max' | 'avg' | 'median';
  readings_considered: 'all_high_fan' | 'last_30_high_fan';
  forecast_method: 'linear' | 'kalman';
  process_noise?: number;
  measurement_noise?: number;
  generate_charts: boolean;
  user_id: number;
  start_date: string;
  end_date: string;
}

// Download CSV Request Types
export interface DownloadCSVRequest {
  job_id?: string;
  device_name: string;
  start_date: string;
  end_date: string;
}

export interface DownloadCSVResponse {
  job_id: string;
  status: 'pending' | 'running' | 'complete' | 'failed';
  processed_days?: number;
  total_days?: number;
  stage?: 'queued' | 'archive' | 'database' | 'upload' | 'complete';
  download_url?: string;
  expires_at?: string;
  filename?: string;
  error?: string;
}

/**
 * FS-825 — the facts a Clip's label is worded from (utils/clipLabel.ts),
 * under the same names on each GET /devices row, on the GET /devices/:id
 * `app_context` and on each system-airflow `clips[]` entry. Optional: an API
 * before FS-825 sends none, and the label is then the description alone.
 */
export interface ClipFilterFields {
  /** The homeowner's name for this Clip's filter spot (the filter record's `location`). */
  filter_location?: string | null;
  /** `airReturns` | `furnace` | null. */
  filter_type?: string | null;
  /** This Clip's 1-based position among the system's return grilles; null for a whole-house filter. */
  filter_slot_ordinal?: number | null;
  /** Return grilles recorded on the system; null when two records name one Clip. */
  hvac_grille_count?: number | null;
  /** Clips on the system, derived from the slot list; null when it was never synced. */
  hvac_clip_count?: number | null;
}

// Device Types
// FS-825: GET /devices sends the ClipFilterFields on each row, for the
// device picker's label. GET /devices/:id sends them inside app_context.
export interface Device extends ClipFilterFields {
  id: number;
  name: string;
  description?: string | null;
  // GET /devices app projection. Optional for an API deployed before FS-1206.
  app_owner_uid?: string | null;
  app_location_id?: string | null;
  app_hvac_id?: string | null;
  created_date: string;
  calibration_offset: number;
  centroids: number[] | null;
  x46_fanside: boolean;
  street_address: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
  latitude: number;
  longitude: number;
  time_zone: string;
  // FS-637: Clip assembly variant from the V-1 Production drawing, one of
  // CLIP_ASSY_VARIANTS (utils/clipAssy.ts) or null when not recorded. The
  // server rejects '' — blank is sent as null.
  assy: string | null;
  // Plan 83: org-scope key (Plan 20 ScopeByEntity). Editable in DeviceList
  // by write_devices users; choices come from entity_path_options.
  entity_path: string | null;
  // Plan 94: resolved Firebase app context from the device_app_context projection,
  // attached by GET /devices/:id. Absent on list endpoints. `status: 'missing'`
  // when the device has no app context (or the projection isn't populated yet).
  app_context?: DeviceSearchAppContext;
  // Retirement flag. Shown prominently wherever a device is listed; editable
  // from the device Settings tab. A decommissioned Clip gets no no-connection
  // alerts, setup reminders or health scoring and is left out of equipment
  // counts; its data is kept.
  decommissioned?: boolean;
  // Lifecycle fields attached by GET /devices/:id. Absent on list endpoints and
  // from an older API. lifecycle_reset_at is set by Return to inventory; a Clip
  // is in inventory while it is set and activated_at is not.
  lifecycle_reset_at?: string | null;
  activated_at?: string | null;
  first_reading_at?: string | null;
  is_in_inventory?: boolean;
  // Uploads discarded since the reset (the Clip sent data while in inventory
  // or before its activation), summed over the current rows.
  discarded_uploads?: DiscardedUploadsSummary;
  // FS-443: per-device filter pressure thresholds, in Pa. Null means unset —
  // end of life falls back to the fleet default, and the notification point
  // falls back to the pipeline's thickness-derived percentage rule. Editable
  // by write_devices from either device-edit surface.
  filter_eol_pa?: number | null;
  filter_notify_pa?: number | null;
  // Measured baseline ΔP at filter change. Read-only here; the edit surfaces
  // show it as context because a valid end-of-life value must sit above it.
  filter_start_dp?: number | null;
}

export interface DiscardedUploadsSummary {
  uploads: number;
  readings: number;
  last_received_at: string | null;
}

// POST /devices/:id/mark-activated response: the device's lifecycle fields.
export interface DeviceLifecycleFields {
  lifecycle_reset_at: string | null;
  activated_at: string | null;
  first_reading_at?: string | null;
  is_in_inventory?: boolean;
}

// Plan 83: row from /devices/entity-path-options (read-only projection
// available to write_devices) and /system/entity-path-options (full CRUD,
// system_admin only).
export interface EntityPathOption {
  path: string;
  label: string | null;
  created_at?: string;
}

export interface DefaultNewDeviceEntityPathSetting {
  path: string | null;
}

// API Response Types
export interface AnalysisMetadataResponse {
  device_name: string;
  id: number;
  oldest_reading_date: string | null;
  latest_processed_date: string | null;
  has_daily_calcs: boolean;
  processed_days: number;
  /** Every processed day (YYYY-MM-DD ascending) for this device+analysis —
   *  lets the date pickers exclude days that have no data. */
  available_dates?: string[];
  // Plan 80
  analysis_key?: string;
  selected_analysis?: AvailableAnalysis | null;
  available_analyses?: AvailableAnalysis[];
  production_filter_health?: ProductionFilterHealth;
}

// Plan 80: Analysis Methods admin panel (device-data-api /analysis-methods).
export interface AnalysisMethod {
  analysis_key: string;
  display_name: string;
  description: string;
  status: 'active' | 'paused' | 'retired';
  handler_key: string;
  default_params: Record<string, unknown>;
  is_default_for_new_devices: boolean;
  display_order: number;
  runnable: boolean;
  assigned_device_count: number;
  supported_side_effects: { side_effect_key: string; default_enabled: boolean }[];
  has_device_dialog: boolean;
}

export interface AnalysisMethodDevice {
  device_id: number;
  device_name: string;
  entity_path: string | null;
  enabled: boolean;
  is_default: boolean;
  params_override: Record<string, unknown>;
  display_name_override: string | null;
  side_effects: { side_effect_key: string; enabled: boolean }[];
  processed_days: number;
  latest_processed_date: string | null;
  latest_error: { date: string; error_message: string } | null;
  model: { model_version: string; updated_through_date: string | null } | null;
}

// Chart Component Types
export interface DataPoint {
  date: string;
  pressure: number;
  trend: number | null;
}

// Device Debug Types
export interface DeviceDebugDevice {
  id: number;
  name: string;
  description: string | null;
  firmware_version: string | null;
  bootloader_version: string | null;
  // FS-948: firmware of the Clip's WiFi module (WIFI_VERSION, Clip firmware
  // 0.19+); free text as reported, null where never reported.
  wifi_version: string | null;
  thing_name: string | null;
  calibration_offset: number;
  x46_fanside: boolean;
  street_address: string | null;
  city: string | null;
  state: string | null;
  postal_code: string | null;
  country: string | null;
  latitude: number | null;
  longitude: number | null;
  time_zone: string | null;
  created_date: string | null;
}

export interface DailyCount {
  date: string;
  count: number;
}

export interface DebugConnectivity {
  last_reading_time: string | null;
  minutes_since_last_reading: number | null;
  daily_counts: DailyCount[];
  total_readings: number;
  first_reading_time: string | null;
}

export interface SensorReading {
  time: string;
  pressure_x46: number;
  pressure_x47: number;
  pressure_diff: number;
  temperature_x46: number | null;
  temperature_x47: number | null;
  temperature: number | null;
  humidity: number | null;
}

export interface TodayMinMax {
  pressure_x46_min: number | null;
  pressure_x46_max: number | null;
  pressure_x47_min: number | null;
  pressure_x47_max: number | null;
  temperature_min: number | null;
  temperature_max: number | null;
  humidity_min: number | null;
  humidity_max: number | null;
}

export interface SensorSnapshot {
  last_readings: SensorReading[];
  today_min_max: TodayMinMax | null;
}

export interface ClockHealth {
  server_time: string;
  last_reading_time: string | null;
  minutes_since_last_reading: number | null;
  connection_status: 'ok' | 'stale' | 'offline' | 'unknown';
  device_clock_time: string | null;
  bundle_received_at: string | null;
  clock_drift_minutes: number | null;
  clock_status: 'ok' | 'minor_drift' | 'clock_error' | 'unknown';
  // Per-packet skew from filter_metrics.clock_skew_seconds (FW v0.07+ TIMENOW
  // vs IoT-rule arrivalTime; null when either field absent).
  latest_skew_seconds: number | null;
  latest_skew_timestamp: string | null;
  skew_24h_avg_seconds: number | null;
  skew_24h_sample_count: number;
}

export interface DebugBattery {
  latest_voltage: number | null;
  latest_timestamp: string | null;
  voltage_7days_ago: number | null;
  timestamp_7days_ago: string | null;
  weekly_delta: number | null;
}

export interface LatestDailyCalc {
  date: string;
  cluster_count: number | null;
  silhouette_score: number | null;
  readings_count: number;
  runtime_minutes: number | null;
  error_message: string | null;
}

export interface DebugPipeline {
  latest_daily_calc: LatestDailyCalc | null;
  processing_gaps: string[];
  s3_bundle_count_today: number;
  s3_bundle_count_yesterday: number;
}

export interface FirmwareHistoryEntry {
  field_name: string;
  old_value: string | null;
  new_value: string;
  observed_at: string;
}

export interface FirmwareHistory {
  entries: FirmwareHistoryEntry[];
}

// FS-1014: where the Clip's latest firmware download stands, from the block
// requests in the IoT Core logs. "downloaded" means every block arrived but
// the Clip has not yet reported the new version; "stalled" means no block
// came for more than stalled_after_hours.
export type FirmwareDownloadState = 'updating' | 'downloaded' | 'done' | 'stalled';

export interface FirmwareDownload {
  state: FirmwareDownloadState;
  version: number;
  version_label: string;
  highest_block: number;
  // Both null when the block count for this version is not known.
  total_blocks: number | null;
  percent: number | null;
  first_block_at: string;
  last_block_at: string;
  stalled_after_hours: number;
}

export type CommEventType =
  | 'activation'
  | 'device_info'
  | 'field_change'
  | 'sensor_bundle'
  | 'firestore';

export interface CommEvent {
  time: string;
  type: CommEventType;
  summary: string;
  detail?: string | null;
  corr?: string | null;
}

export interface CommHistory {
  events: CommEvent[];
}

export interface IoTTrailEvent {
  time: string;
  event_type: string;
  topic: string;
  rule: string;
  status: string;
  payload?: string | null;
}

export interface IoTTrailSection {
  resolved_thing_name: string | null;
  source: string;
  window_days: number;
  events: IoTTrailEvent[];
  // FS-1001: most events the query returns (newest first), and how many
  // matched in the window before that limit. Absent from older Lambdas;
  // matched is null when CloudWatch returned no statistics.
  limit?: number;
  matched?: number | null;
}

export interface DeviceDebugResponse {
  device_name: string;
  id: number;
  generated_at: string;
  device: DeviceDebugDevice;
  connectivity: DebugConnectivity;
  sensor_snapshot: SensorSnapshot;
  clock_health: ClockHealth | null;
  battery: DebugBattery;
  pipeline: DebugPipeline;
  firmware_history: FirmwareHistory | null;
  // FS-1014: null when the device has no recorded download; absent from
  // older Lambdas.
  firmware_download?: FirmwareDownload | null;
  comm_history: CommHistory | null;
  iot_trail?: IoTTrailSection | null;
  anomalies: string[];
  errors: string[];
}

export interface S3BundleSummary {
  key: string;
  last_modified: string;
  size: number;
  etag: string;
}

export interface S3BundleListResponse {
  device_name: string;
  prefix: string;
  bundles: S3BundleSummary[];
}

export interface S3BundleDetailResponse {
  device_name: string;
  key: string;
  last_modified: string;
  size: number;
  content_kind: 'json' | 'text' | 'base64';
  content: string;
}

// Device Notes (Plan 45 — shown as markers on analysis charts and as rows on /notes page)

// Reference to one attachment row in note_images. The raw S3 URL never
// reaches the client — each image is fetched via its presigned-URL endpoint.
export interface NoteImageRef {
  id: number;
}

export interface Note {
  id: number;
  device_id: number;
  user_id: number;
  note_text: string;
  created_at: string;
  creator_email?: string | null;
  filter_changed: boolean;
  images?: NoteImageRef[];
}

export type NotesByDeviceId = Record<number, Note[]>;

// Firestore Enrichment Types
//
// FS-1033: every caller gets only these owner fields; email requires full
// device scope `/`. A missing owner is {}, so each field can be absent.
// FS-908: root still gets the whole location document, while a narrower
// caller gets only { id, name } or {} when the location is missing.
export interface FirestoreUser {
  id?: string;
  /** Root callers only. */
  email?: string;
  firstname?: string;
  lastName?: string;
}

export interface FirestoreLocation {
  id: string;
  /** Root callers only, as are zipCode, lastKnownDeviceName and isSetupComplete. */
  userId?: string;
  name?: string;
  zipCode?: string;
  lastKnownDeviceName?: string;
  isSetupComplete?: boolean;
}

export interface FirestoreHvacSystem {
  id: string;
  name?: string;
  systemType?: string;
  manufacturer?: string;
  modelNumber?: string;
  /** Every label photo on the record. The operator route appends to this. */
  labelPhotoUrls?: string[];
  /** The first one only, and only on records written before the array existed. */
  labelPhotoUrl?: string;
  /** One per label read, carrying the converted copy when there is one. */
  labelPhotoAnalyses?: FirestorePhotoAnalysis[];
  /** FS-780: the drawable copy of each converted photo on this record. */
  labelPhotoPreviews?: FirestorePhotoPreview[];
}

/**
 * A Firestore date as `/fb/devices/enrich` passes it through. The admin SDK's
 * Timestamp has no toJSON, so it arrives as its two private fields; the app's
 * reader also accepts an ISO string or epoch milliseconds, which older records
 * may hold instead.
 */
export type FirestoreDateValue =
  | { _seconds: number; _nanoseconds?: number }
  | { seconds: number; nanoseconds?: number }
  | string
  | number
  | null;

export interface FirestoreFilter {
  id: string;
  deviceName?: string;
  /** Label photos on the filter record; `photoUrls` below is its own gallery. */
  labelPhotoUrls?: string[];
  labelPhotoUrl?: string;
  /** One per label read, carrying the converted copy when there is one. */
  labelPhotoAnalyses?: FirestorePhotoAnalysis[];
  /** FS-780: the drawable copy of each converted photo on this record. */
  labelPhotoPreviews?: FirestorePhotoPreview[];
  /** The filter's own photo gallery, which is not label photos. */
  photoUrls?: string[];
  manufacturer?: string;
  rating?: string;
  length?: number;
  width?: number;
  thickness?: number;
  // App-facing lifecycle fields (written by process-firestore-updates).
  status?: string; // calibrating | normal | change_soon | change_now
  predictedChangeDate?: string;
  daysRemaining?: number | null;
  replacementNeeded?: boolean;
  installDate?: FirestoreDateValue;
}

// The Firestore device doc (users/{uid}/.../devices/{did}). airflowPercentageFloor
// is the monotonic filter % the mobile app actually renders to the homeowner
// (Plan 26); airflowPercentage is the raw pre-floor value.
export interface FirestoreDevice {
  id?: string;
  deviceName?: string;
  airflowPercentage?: number | null;
  airflowPercentageFloor?: number | null;
  batteryLevel?: number | null;
  batteryLevelFloor?: number | null;
  firmwareVersion?: string;
  isConnected?: boolean;
  [key: string]: any;
}

/**
 * One entry of a record's `labelPhotoAnalyses`, as `/fb/devices/enrich` passes
 * it through from Firestore. `analyzedUrl` and `derivative` are written only
 * when the label read had to convert the upload to read it — an iPhone HEIC,
 * which the browser cannot draw, so this is the copy the console shows when
 * the record carries no `labelPhotoPreviews` entry for the photo.
 */
export interface FirestorePhotoAnalysis {
  url?: string;
  analyzedUrl?: string;
  derivative?: { format?: string; from?: string };
  analyzedAt?: any;
  requestedBy?: string;
  [key: string]: any;
}

/**
 * FS-780: one entry of a record's `labelPhotoPreviews` — the browser-drawable
 * copy of a converted photo (a JPEG of a HEIC). The Functions write one on
 * EVERY record the photo is attached to, including a record the label read
 * routed it to, as soon as the conversion succeeds and whether or not the read
 * then does. A re-read can add another for the same `url`; the newest `at`
 * wins. Drawn ahead of an analysis's `analyzedUrl`.
 */
export interface FirestorePhotoPreview {
  /** The original upload, exactly as it sits in `labelPhotoUrls`. */
  url?: string;
  previewUrl?: string;
  /** `jpeg`. */
  format?: string;
  /** The original's format, e.g. `heic`. */
  from?: string;
  at?: FirestoreDateValue;
  [key: string]: any;
}

export interface FirestoreAcSystem {
  id?: string;
  hvacSystemId?: string;
  name?: string;
  modelNumber?: string;
  systemType?: string;
  /** Every label photo on the record. The operator route appends to this. */
  labelPhotoUrls?: string[];
  /** The first one only, and only on records written before the array existed. */
  labelPhotoUrl?: string;
  labelPhotoAnalyses?: FirestorePhotoAnalysis[];
  /** FS-780: the drawable copy of each converted photo on this record. */
  labelPhotoPreviews?: FirestorePhotoPreview[];
  aiAnalyzed?: boolean;
  [key: string]: any;
}

// An open alert doc on a device's current filter, as /fb/devices/enrich returns
// it — type + severity only, the two fields the app's status logic reads.
export interface FirestoreFilterAlert {
  type: string;
  severity: string;
}

export interface DeviceEnrichment {
  user: FirestoreUser;
  location: FirestoreLocation;
  // FS-908: for a root caller, every HVAC system at the device's location and
  // every A/C system under each. For any other caller, the device's own HVAC
  // system only (one entry, or none when its document is missing) and that
  // system's A/C systems — never the location's other systems.
  hvacSystems: FirestoreHvacSystem[];
  acSystems: FirestoreAcSystem[];
  filter: FirestoreFilter;
  // Open alerts on the CURRENT filter. Optional: responses from a firebase-api
  // older than the FS-529 review fix don't carry the field.
  filterAlerts?: FirestoreFilterAlert[];
  // FS-539 — the device's EARLIER filter records, newest install first. Only
  // the single-device read (/fb/devices/enrich/:deviceName) carries it; the
  // batch map never does, and a firebase-api older than FS-539 leaves it out.
  previousFilters?: FirestoreFilter[];
  device: FirestoreDevice;
}

export type DeviceEnrichmentMap = Record<string, DeviceEnrichment>;

/**
 * Plan 119 Phase 3 (FS-627) — the equipment model catalog. One row per
 * (manufacturer, normalized model number), the manufacturer documents behind
 * it, and the facts read out of them. Read-only from the console.
 */
export type EquipmentLookupStatus =
  | 'queued'
  | 'resolved'
  | 'ambiguous'
  | 'no_source'
  | 'not_a_model'
  // FS-786: a manufacturer document was found and cached, and its reading
  // kept no fact. Finished research, not `no_source` and not resolved.
  | 'document_no_facts';

/** The document a catalog fact was read out of, as the API returns it. */
export interface EquipmentDocumentRef {
  id?: number | null;
  title?: string | null;
  revision?: string | null;
  source_url?: string | null;
}

export interface EquipmentDocument extends EquipmentDocumentRef {
  id: number;
  source_url: string;
  retrieved_at: string | null;
  content_type: string | null;
  pages: number | null;
  byte_size: number | null;
}

/**
 * GET /equipment/documents/:id/file — a short-lived link to the copy we keep
 * of a manufacturer document. The URL is presigned, so it carries its own
 * authorization and must be opened as-is, without the console's headers.
 */
export interface EquipmentDocumentFile {
  url: string;
  /** Seconds the link stays good for. */
  expires_in: number;
  content_type: string | null;
  byte_size: number | null;
  title: string | null;
}

export interface EquipmentModelFact {
  id: number;
  fact: string;
  value_text: string | null;
  value_num: number | null;
  value_json: Record<string, unknown> | null;
  unit: string | null;
  /** `rating_point` or `furnace_limit`, and only on a rated_esp fact. */
  kind: string | null;
  confidence: string | null;
  /** Where in the document the fact was read, e.g. `p.6`. */
  locator: string | null;
  applicability: string | null;
  verified_by: string | null;
  verified_at: string | null;
  document: EquipmentDocument | null;
}

export interface EquipmentModelAlias {
  alias_normalized: string;
  reason: string | null;
  created_at: string | null;
}

export interface EquipmentModel {
  id: number;
  manufacturer: string;
  model_normalized: string;
  model_display: string;
  product_line: string | null;
  equipment_type: string;
  lookup_status: string;
  notes: string | null;
  verified_by: string | null;
  verified_at: string | null;
  /** Convenience projections of the facts; null when there is no such fact. */
  motor_family?: string | null;
  rated_esp_pa?: number | null;
  cooling_tonnage?: number | null;
}

/** A listing row — the model plus how much the catalog holds for it. */
export interface EquipmentModelRow extends EquipmentModel {
  fact_count: number;
  alias_count: number;
  document_count: number;
}

export interface EquipmentModelsResponse {
  total: number;
  limit: number;
  offset: number;
  rows: EquipmentModelRow[];
}

export interface EquipmentModelDetail {
  model: EquipmentModel;
  aliases: EquipmentModelAlias[];
  facts: EquipmentModelFact[];
  documents: EquipmentDocument[];
}

/**
 * GET /devices/inventory — one row per (device, field): do we have it, where
 * did it come from, how good is it, and what happens next. Everything is
 * computed by the server on read, so the Equipment Inventory page and the
 * device Overview cannot word the same fact differently.
 *
 * Lives here rather than on the page because two screens read it: the fleet
 * list, and the single-device section on the device workspace.
 */
export interface EquipmentInventoryRow {
  device_id: number;
  device_name: string;
  entity_path: string | null;
  owner_email_present: boolean;
  app_hvac_id: string | null;
  system_key: string | null;
  claim_status: string;
  field: string;
  state: string;
  value: string | null;
  unit: string | null;
  source: string | null;
  component: string | null;
  quality: string;
  quality_rank: number;
  eligible: boolean;
  /**
   * What the row is worth in the operator's own words, including the
   * manufacturer document a fact was read out of. It is the only place a
   * document citation appears, so it is never truncated on screen.
   */
  evidence: string | null;
  route: string;
  route_reason: string;
  shared_record: boolean;
  /**
   * Row contract version 4: the old `last_verified_at` under the name it
   * always deserved. It is when the fact was RECORDED — nothing re-verifies
   * it.
   */
  recorded_at: string | null;
  /**
   * When the nightly job last rewrote this device's projection. A fact
   * recorded in July on a projection rewritten last night is worth more than
   * the same fact on one that has not been touched for a week, and the two
   * dates are the only way to tell those apart.
   */
  projection_updated_at: string | null;
}

export interface EquipmentInventoryResponse {
  total: number;
  limit: number;
  offset: number;
  cohort: string;
  version: number;
  rows: EquipmentInventoryRow[];
}

// Plan 94 Phase 4: GET /devices/search response (device-data-api). Scope-checked
// device search backed by the device_app_context projection — never live Firestore.
export type AppContextStatus = 'missing' | 'current' | 'ambiguous' | 'stale';

// FS-825: the ClipFilterFields arrive on the device detail's app_context only.
export interface DeviceSearchAppContext extends ClipFilterFields {
  status: AppContextStatus;
  /**
   * When this Clip's projection row was last rewritten (ISO, the database's
   * clock), on the device detail only. FS-952: a page that reads only the
   * device follows a write by it.
   */
  projection_updated_at?: string | null;
  // FS-566: the system tonnage the server selected (the same object the
  // system-airflow endpoint returns as `tonnage`), on the device detail only.
  // Absent from an API that predates FS-566; the HVAC card then reads the
  // hvac_tonnage fields as before.
  system_tonnage?: SystemTonnage | null;
  owner_name: string | null;
  owner_email: string | null;
  location_name: string | null;
  hvac_name: string | null;
  filter_summary: string | null;
  // Plan 100 enrichment specs (GET /devices/:id only; optional so pre-Plan-100
  // API responses still typecheck)
  hvac_manufacturer?: string | null;
  hvac_model?: string | null;
  hvac_tonnage?: number | null;
  hvac_btu_rating?: number | null;
  hvac_blower_type?: string | null;
  ac_name?: string | null;
  ac_manufacturer?: string | null;
  ac_model?: string | null;
  ac_seer?: number | null;
  ac_compressor_stages?: string | null;
  // Plan 119 Phase 1 (FS-618): where each value above came from, off which
  // component, how confident and when. Optional so a response from an API that
  // predates the projection change still typechecks.
  hvac_tonnage_source?: string | null;
  hvac_tonnage_side?: string | null;
  hvac_tonnage_confidence?: number | null;
  hvac_tonnage_at?: string | null;
  hvac_btu_rating_source?: string | null;
  hvac_btu_rating_side?: string | null;
  hvac_btu_rating_confidence?: number | null;
  hvac_btu_rating_at?: string | null;
  hvac_blower_type_source?: string | null;
  hvac_blower_type_side?: string | null;
  hvac_blower_type_confidence?: number | null;
  hvac_blower_type_at?: string | null;
  ac_seer_source?: string | null;
  ac_seer_side?: string | null;
  ac_seer_confidence?: number | null;
  ac_seer_at?: string | null;
  ac_compressor_stages_source?: string | null;
  ac_compressor_stages_side?: string | null;
  ac_compressor_stages_confidence?: number | null;
  ac_compressor_stages_at?: string | null;
  hvac_equipment_side?: string | null;
  ac_equipment_side?: string | null;
  hvac_capacity_source?: string | null;
  ac_capacity_source?: string | null;
  // How capacity was arrived at on the record that supplied the TONNAGE. The
  // two columns above describe their own records; this one belongs to the
  // selected value, and is what the quality ladder reads (P119-C04).
  hvac_tonnage_capacity_source?: string | null;
  // Label photos on file per record. 0 and null differ: 0 is a record with no
  // photo, null is no record at all.
  hvac_photo_count?: number | null;
  ac_photo_count?: number | null;
  filter_photo_count?: number | null;
  hvac_enrichment_version?: number | null;
  // Plan 119 Phase 2 (FS-460): the quality ladder, computed on the server so
  // the Overview and the Device List cannot describe the same value
  // differently. Optional for the same reason as the provenance above.
  hvac_tonnage_quality?: string | null;
  hvac_tonnage_quality_rank?: number | null;
  hvac_blower_type_quality?: string | null;
  hvac_blower_type_quality_rank?: number | null;
  filter_quality?: string | null;
  filter_quality_rank?: number | null;
  missing_count?: number | null;
  missing_fields?: string | null;
  // FS-752 — the furnace or air handler record's quality code, read when the
  // API sends it so the Overview's Missing info line can give its reason.
  indoor_record_quality?: string | null;
  // Plan 119 Phase 3 (FS-627): what the equipment model catalog resolved for
  // this system. Optional for the same reason as everything above it — a
  // response from an API that predates the projection change still typechecks.
  // The catalog id is a bigint in Postgres, so /devices/search sends it as a
  // string ("3") while the single-device read can send a number. Typed as
  // both, because it arrives as both — never compared with ===.
  hvac_catalog_model_id?: number | string | null;
  ac_catalog_model_id?: number | string | null;
  hvac_catalog_manufacturer?: string | null;
  hvac_catalog_equipment_type?: string | null;
  ac_catalog_equipment_type?: string | null;
  // The manufacturer's published external static pressure. `_pa` is the
  // converted number and the presence test; `_original` is the manufacturer's
  // own wording, which is what an operator should read. This is an operating
  // point, never a filter end-of-life threshold.
  hvac_rated_esp_pa?: number | null;
  hvac_rated_esp_original?: string | null;
  hvac_rated_esp_kind?: string | null;
  hvac_rated_esp_document_id?: number | null;
  hvac_rated_esp_confidence?: number | null;
  hvac_rated_esp_at?: string | null;
  hvac_blower_type_document_id?: number | null;
  // Plan 119 Phase 4: who said what the FILTER is, and what its catalog match
  // was worth. `filter_identity_source` is the server's one answer across the
  // four identity fields — a label reading beats a person's entry, because it
  // is the one that carries evidence. The `filter_catalog_*` five come off the
  // device row, so they are readable even when the app claim is not.
  filter_model?: string | null;
  filter_identity_source?: string | null;
  filter_manufacturer_source?: string | null;
  filter_manufacturer_at?: string | null;
  filter_rating_source?: string | null;
  filter_rating_at?: string | null;
  filter_size_source?: string | null;
  filter_size_at?: string | null;
  filter_model_source?: string | null;
  filter_model_at?: string | null;
  filter_enrichment_version?: number | null;
  /**
   * Plan 120 — the evaporator coil as a component of its own.
   *
   * Until now a coil could only appear as a side stamp on the one HVAC
   * record, which fused two machines into one: a coil model in the identity
   * slot with a furnace's blower type beside it. The coil has its own
   * Firestore child and its own projection columns now, and the Overview
   * draws it as its own card.
   *
   * Every one of these is null on a system with no coil child, which is every
   * system until the Plan 120 migration runs. `coil_count` is what says a
   * card should be drawn; `coil_model` may still be null inside it.
   *
   * `coil_catalog_model_id` is a Postgres bigint, so it arrives as a string
   * from /devices/search and can arrive as a number from the single-device
   * read — the same shape as the two catalog ids above, and never compared
   * with ===.
   *
   * `hvac_unclassified_photo_count` counts photos on the HVAC record the
   * analysis could not place on a component.
   * `hvac_component_review_reason` is the legacy guard's hold, one of
   * `legacy_guard_pending`, `legacy_identity_held` or
   * `typed_coil_model_on_parent`.
   */
  coil_id?: string | null;
  coil_model?: string | null;
  coil_manufacturer?: string | null;
  coil_tonnage?: number | string | null;
  coil_tonnage_source?: string | null;
  coil_tonnage_at?: string | null;
  coil_tonnage_confidence?: number | null;
  coil_equipment_side?: string | null;
  coil_catalog_model_id?: number | string | null;
  coil_photo_count?: number | null;
  coil_count?: number | null;
  hvac_unclassified_photo_count?: number | null;
  hvac_component_review_reason?: string | null;
  /**
   * The catalog row the nightly match chose, carried here as well as on the
   * device row so one read answers "is there a match at all" — a device
   * matched before Phase 4 has an id and no lineage, and must not read as
   * unmatched.
   */
  filter_catalog_id?: number | null;
  /** The source the MATCH pinned, not what the catalog row says today. */
  filter_catalog_source?: string | null;
  filter_catalog_confidence?: string | null;
  filter_catalog_size_verified?: boolean | null;
  filter_catalog_matched_at?: string | null;
  filter_catalog_updated_at?: string | null;
  /** A homeowner-entered motor family a catalog fact contradicts. */
  hvac_blower_type_conflict?: boolean | null;
  /**
   * The motor family the manufacturer's document states, off the same fact
   * row as `hvac_blower_type_locator`. Null when no catalog fact sits behind
   * the conflict, which is why the conflict sentence has a wording for that.
   */
  hvac_blower_type_catalog_value?: string | null;
  hvac_tonnage_document_id?: number | null;
  /**
   * FS-835/FS-838 — which unit gave the system tonnage, and what credible
   * value disagrees with it. The console renders these and never compares
   * two tonnages itself. Optional for the same reason as everything above.
   */
  hvac_tonnage_component?: string | null;
  hvac_tonnage_conflict?: boolean | null;
  hvac_tonnage_alt?: number | null;
  hvac_tonnage_alt_side?: string | null;
  hvac_tonnage_alt_source?: string | null;
  hvac_tonnage_alt_document_id?: number | string | null;
  /** Resolved on the detail endpoint only, like the rated-pressure document. */
  hvac_tonnage_document?: EquipmentDocumentRef | null;
  hvac_tonnage_locator?: string | null;
  hvac_tonnage_alt_document?: EquipmentDocumentRef | null;
  hvac_tonnage_alt_locator?: string | null;
  /**
   * FS-951 — the model whose document states the disagreeing value, and the
   * records (`hvac`, `ac`, `coil`) that carry it. Null unless a record names
   * that model; absent from an API before FS-951.
   */
  hvac_tonnage_alt_model?: { model_id: string; model: string; records: string[] } | null;
  /** FS-838 — how the coil record's own capacity was arrived at. */
  coil_capacity_source?: string | null;
  rated_static_quality?: string | null;
  rated_static_quality_rank?: number | null;
  // Resolved on the detail endpoint only: the document row behind each
  // catalog-sourced fact, and where in it the fact was read.
  hvac_rated_esp_document?: EquipmentDocumentRef | null;
  hvac_rated_esp_locator?: string | null;
  hvac_blower_type_document?: EquipmentDocumentRef | null;
  hvac_blower_type_locator?: string | null;
}

// Plan 33 Phase 6 (first pass): GET /devices/:id/system-airflow — the Clips
// sharing this device's HVAC system with each Clip's clean-filter CFM
// estimate, or the reason it cannot contribute to the system total.
//
// FS-666: the per-Clip value is the average of the Clip's first up-to-three
// computed days in its current filter period — an estimate of the system's
// airflow on a clean filter — not its latest day, which drifts up as the
// filter loads.
//
// FS-747: the per-Clip value is the mean CFM of the first three RUNNING days
// (a day of the default analysis with a fan-on top level, not quarantined)
// in the 14 days from the Clip's first running day in this filter period
// (Plan 127 Amendments 1 and 3: a Clip that reports late, or reports only
// fan-off days at first, gets its 14 days from its first running day, not
// from the change; FS-848). A running day with no CFM still
// takes its slot, so a late CFM never pulls in a loaded filter's day.
// 'no_clean_airflow' (FS-747): the window closed, a curve matched, and no
// clean day had a CFM.
export type SystemAirflowClipStatus =
  | 'ok'
  | 'no_filter_info'
  | 'no_curve_match'
  | 'not_computed'
  | 'no_clean_airflow';

/** FS-747 / FS-848 — the 14-day clean-filter window, from the Clip's first running day in this filter period. */
export interface SystemAirflowCleanWindow {
  period_start: string | null; // YYYY-MM-DD
  window_end: string | null; // YYYY-MM-DD, inclusive
  /** 'open' while still collecting; 'closed' once 3 running days were found or the window ended. */
  state: 'open' | 'closed' | null;
  running_days: 0 | 1 | 2 | 3;
  /** The clean (running) days used, ascending, including any without a CFM. */
  dates: string[];
  /** The first day the window could use, YYYY-MM-DD. */
  window_start?: string | null;
  /** Days between the filter change and the Clip's first report; > 0 means the
   *  clean days may not be a clean filter's. Absent on older responses. */
  reporting_lag_days?: number | null;
}

export type AirflowCalibrationState = 'calibrated' | 'partly' | 'approximate';

/** FS-747 — why a value is not calibrated, from the device's current match lineage (FS-648). */
export type AirflowCalibrationReason =
  | 'size_not_verified'
  | 'product_not_resolved'
  | 'no_calibrated_day'
  | 'not_certified'
  | 'unknown'
  | 'no_match';

/** FS-747 — how far the Clip's value can be trusted, and the curve it came from. */
export interface SystemAirflowCalibration {
  /** Null only when there is no value. */
  state: AirflowCalibrationState | null;
  /** Clean days with a CFM that were calibrated. */
  calibrated_days: number;
  /** Null when calibrated. */
  reason: AirflowCalibrationReason | null;
  /** filter_catalog source tier of the current match ('maedb', 'certified_scaled', 'extrapolated', ...). */
  source: string | null;
  confidence: 'high' | 'low' | null;
  size_verified: boolean | null;
  /** 'certified_scaled': the certified size the curve was scaled from. */
  scaled_from: string | null;
  /** 'extrapolated': the sibling filter the curve was scaled from. */
  donor: string | null;
}

// The pre-FS-666 API also sent 'stale' (an estimate older than 14 days). The
// new API never does, but a response from it can still arrive during the
// deploy gap or after a rollback; SystemAirflowSection's legacy guard is
// what keeps such a payload off the page, and this is the name it needs.
export type LegacySystemAirflowClipStatus = SystemAirflowClipStatus | 'stale';

// FS-825: each entry carries the Clip's own ClipFilterFields, for its label.
export interface SystemAirflowClip extends ClipFilterFields {
  device_id: number;
  name: string;
  description: string | null;
  status: SystemAirflowClipStatus;
  /** Unrounded mean of the qualifying days; null unless status is 'ok'. */
  clean_cfm: number | null;
  /**
   * How many days went into clean_cfm. Present on EVERY entry of an FS-666
   * response, contributors or not — 0 is a valid value, not a missing one.
   * Its absence is how the client recognizes a pre-FS-666 payload.
   */
  clean_cfm_days: 0 | 1 | 2 | 3;
  clean_cfm_first_date: string | null; // YYYY-MM-DD
  clean_cfm_last_date: string | null; // YYYY-MM-DD
  /** @deprecated FS-666 alias of clean_cfm; read clean_cfm instead. */
  latest_cfm: number | null;
  /** @deprecated FS-666 alias of clean_cfm_last_date. */
  latest_cfm_date: string | null; // YYYY-MM-DD
  // FS-566. Optional: a pre-FS-566 API does not send them, and the client
  // then reads description/name, clean_cfm and status === 'ok' instead.
  device_name?: string | null;
  /** The unrounded clean_cfm; null when the Clip has none. */
  cfm?: number | null;
  /** Whether cfm went into the system figure (server: status === 'ok'). */
  contributed?: boolean;
  // FS-721. Sent on every entry by an FS-721 API; a response without
  // face_velocity_missing on any entry predates it, and the per-filter Size
  // and Face velocity columns are then left off. The client never divides.
  /** Nominal filter size in inches; null unless both sides are over 1.5 in. */
  filter_size?: { width_in: number; length_in: number } | null;
  /** (width − 1.5) × (length − 1.5) / 144, unrounded; null without a size. */
  filter_net_area_sqft?: number | null;
  /** round(cfm ÷ filter_net_area_sqft), ft/min; null when either is missing. */
  face_velocity_fpm?: number | null;
  /** What kept face_velocity_fpm null, in this order; [] when it is present. */
  face_velocity_missing?: Array<'filter_size' | 'airflow'>;
  /** True only when every day behind cfm was calibrated; null with no days.
   *  FS-747: `calibration.state` says the same and more; the client reads
   *  that when present and this only for an API before FS-747. */
  cfm_calibrated?: boolean | null;
  // FS-747. Optional: an API before FS-747 does not send them, and the
  // client then renders exactly what it did before.
  clean_window?: SystemAirflowCleanWindow;
  calibration?: SystemAirflowCalibration;
  // Plan 127 Phase 3. Optional: an API before Phase 3 does not send them.
  /** 'record': the stored filter_period_airflow record; 'live': no record
   *  yet (window open or not started), the FS-747 selection. */
  source?: 'record' | 'live';
  /** Where the pressure sat on the filter curve; null when there is no value. */
  range_status?: AirflowRangeStatus | null;
  /** The record behind a 'record' value; null for 'live'. */
  record?: { revision: number; computed_at: string; analysis_key: string } | null;
}

/**
 * Plan 127 Phase 3 — where the clean days' pressure sat on the filter curve:
 * inside it, below its lowest point (the CFM is the curve's minimum), above
 * its highest point (the curve's maximum), or on a single-point curve.
 */
export type AirflowRangeStatus = 'in_range' | 'clamped_low' | 'clamped_high' | 'power_law';

/**
 * Plan 127 Phase 3 — fetch_analysis's stored airflow record for the
 * SELECTED filter period: the one airflow value for (device, period_start),
 * with the match it was computed on (a snapshot, not the device's match
 * today). `state` is never 'partly': a record is computed on one match.
 */
export interface PeriodAirflowRecord {
  period_start: string; // YYYY-MM-DD
  window_start: string | null; // YYYY-MM-DD
  reporting_lag_days: number | null;
  /** The clean (running) days, ascending, at most three. */
  clean_dates: string[];
  running_days: number;
  /** Unrounded, as stored; null when no clean day had a CFM. */
  cfm: number | null;
  calibrated: boolean | null;
  state: 'calibrated' | 'approximate' | null;
  reason: AirflowCalibrationReason | null;
  range_status: AirflowRangeStatus | null;
  catalog_id: number | null;
  catalog_source: string | null;
  catalog_confidence: 'high' | 'low' | null;
  catalog_size_verified: boolean | null;
  analysis_key: string;
  revision: number;
  computed_at: string;
}

export interface SystemAirflowResponse {
  hvac_id: string | null;
  hvac_name: string | null;
  hvac_manufacturer: string | null;
  hvac_model: string | null;
  hvac_tonnage: number | null;
  hvac_btu_rating: number | null;
  clips: SystemAirflowClip[];
  clip_count: number;
  hidden_count: number; // group members outside the caller's entity scope
  total_cfm: number | null; // sum of contributing clips' clean_cfm, rounded once; null when none
  contributing_count: number;
  complete: boolean;
  /** @deprecated FS-666 — still sent by the API, but nothing is filtered by it. */
  recency_days: number;
  // FS-566 (with FS-560). Every field below is optional: a response from the
  // API before FS-566 carries none of them, and the Overview must then render
  // exactly what it rendered before. Their presence is how the client tells
  // the two shapes apart (isSystemAirflowV2 in SystemAirflowSection).
  /** Sum of the contributing members' estimates when NOT complete; null otherwise. */
  partial_cfm?: number | null;
  /** The system tonnage the server selected, with where it came from. */
  tonnage?: SystemTonnage | null;
  /**
   * Server-computed; present only when complete and the tonnage is eligible. Never divided client-side.
   * (FS-878: a PAST period's CFM per ton is divided in the client, from its stored record and this
   * response's `tonnage`, by the server's rule; see SystemAirflowSection `pastPeriodPerTon`.)
   */
  cfm_per_ton?: number | null;
  cfm_per_ton_reason?: CfmPerTonReason | null;
  projection_stale?: boolean;
  // FS-593 (step S3). Optional for the same reason: an API before FS-593
  // sends none of them, which reads the same as `coverage: null`.
  /** Every filter slot on the system, in the projection's stored order. */
  slots?: SystemFilterSlot[];
  /** Slots per state; null when the projection has no slot list yet. */
  coverage?: SystemFilterCoverage | null;
  /** Slots typed as return grilles; null when two records name one Clip. */
  recorded_grille_count?: number | null;
  /** When this Clip's app projection was last synced (ISO), or null. */
  projection_updated_at?: string | null;
  /** FS-747: calibration over the contributing Clips; null when none contributes. Absent before FS-747. */
  calibration_state?: AirflowCalibrationState | null;
}

/**
 * FS-593 — one filter slot: a return grille or the furnace filter. `state`
 * says how the slot relates to the Clips on the system:
 * - `member`: a Clip on this system that the caller can see sits in it;
 * - `hidden`: a Clip outside the caller's scope sits in it (its id and serial
 *   are withheld);
 * - `none`: no Clip is recorded in it;
 * - `unresolved`: it names a Clip that is not on this system;
 * - `conflict`: two or more slots name the same Clip.
 * `device_id` / `device_name` are set only for `member` and visible `conflict`.
 */
export type SystemFilterSlotState = 'member' | 'hidden' | 'none' | 'unresolved' | 'conflict';

export interface SystemFilterSlot {
  id: string;
  type: 'airReturns' | 'furnace' | null;
  location: string | null;
  has_sensor: boolean | null;
  state: SystemFilterSlotState;
  device_id: number | null;
  device_name: string | null;
  contributed: boolean | null;
}

export interface SystemFilterCoverage {
  complete: boolean;
  member: number;
  hidden: number;
  none: number;
  unresolved: number;
  conflict: number;
}

/**
 * FS-566 — the tonnage the server selected for a whole HVAC system. The same
 * object is on GET /devices/:id/system-airflow (`tonnage`) and on the device
 * detail (`system_tonnage`), so the HVAC card and the airflow headline read
 * one value and cannot disagree.
 */
export interface SystemTonnage {
  value: number | null;
  candidate: 'hvac' | 'coil' | null;
  /** A provenance source code (see sourceLabel), or null. */
  source: string | null;
  /** A server quality code (see qualityLabel), or null. */
  quality: string | null;
  /** Whether the value may be used for CFM/ton. */
  eligible: boolean;
  reason: null | 'no_tonnage' | 'tonnage_from_btu' | 'projection_stale';
  /**
   * FS-835/FS-838 — the projection's own account of the winner. All optional,
   * so a response from an API before FS-835 still type-checks and renders.
   * `component` is the fixture vocabulary (`air_handler`, `indoor_coil`,
   * `outdoor_unit`, …) and `side` the `equipmentSide` one; both null without
   * a value.
   */
  component?: string | null;
  side?: string | null;
  /** `app_hvac_tonnage_conflict`: null no value, false agreed, true disputed. */
  conflict?: boolean | null;
  /** The best disagreeing value the resolver projected beside the winner. */
  alt?: SystemTonnageAlt | null;
  /** The value is a blower unit's "up to N tons": a maximum, not a size. */
  upper_bound?: boolean;
  /**
   * FS-838 review F1/F2 — the winner's own capacity source and document,
   * read when an API carries them on the snapshot. Absent today: the console
   * then takes them from the same response the winner came from, or says
   * they are not recorded.
   */
  capacity_source?: string | null;
  document_id?: number | string | null;
}

/** FS-838 — the disagreeing value, named only by the side it was read off. */
export interface SystemTonnageAlt {
  value: number;
  side: string | null;
  source: string | null;
  document_id: number | string | null;
}

export type CfmPerTonReason =
  | 'projection_stale'
  | 'no_tonnage'
  | 'tonnage_from_btu'
  | 'no_airflow'
  | 'partial_airflow';

// Plan 101: the full wide row — every per-Clip column the Fleet Explorer can
// show. Dates arrive as plain YYYY-MM-DD strings (server to_char), last_heard
// as a compact UTC ISO stamp; derived telemetry (days_since_heard, 7-day
// pressure aggregates, battery_pct) is server-computed so filter/sort run in
// SQL. `app_context` mirrors the flat app_* fields (kept nested for pre-101
// consumers like the cleanup console).
export interface DeviceSearchRow {
  id: number;
  name: string;
  description: string | null;
  thing_name: string | null;
  entity_path: string | null;
  decommissioned: boolean;
  created_date: string | null;
  // Device first-activation date (YYYY-MM-DD), or null. This is the Plan 87
  // activated_at stamp — set once when the device first comes online and never
  // moved by filter changes; cleared to null by return-to-inventory. Null also
  // for devices activated before Plan 87 shipped (no backfill).
  activated_at: string | null;
  install_date: string | null;
  first_reading_at: string | null;
  // FS-777 — the remaining lifecycle dates (YYYY-MM-DD) and the yes/no twins.
  // Optional: an API from before FS-777 does not send them, and the cells
  // then show a dash.
  is_activated?: boolean;
  /** Creation time of the app record the projection selected. */
  registered_at?: string | null;
  is_registered?: boolean;
  has_first_reading?: boolean;
  /** Earliest install date among the Clip's current filter records. */
  first_filter_installed?: string | null;
  is_installed?: boolean;
  /** When the Clip was last returned to inventory. */
  lifecycle_reset_at?: string | null;
  is_in_inventory?: boolean;
  street_address: string | null;
  city: string | null;
  state: string | null;
  postal_code: string | null;
  country: string | null;
  time_zone: string | null;
  latitude: number | null;
  longitude: number | null;
  x46_fanside: boolean | null;
  calibration_offset: number;
  firmware_version: string | null;
  bootloader_version: string | null;
  // FS-948: firmware of the Clip's WiFi module (WIFI_VERSION, Clip firmware
  // 0.19+); enum-filterable via the `wifi_version` facet.
  wifi_version: string | null;
  // FS-637: Clip assembly variant (see Device.assy); enum-filterable via the
  // `assy` facet.
  assy: string | null;
  battery_voltage: number | null;
  battery_pct: number | null;
  airflow_percentage: number | null;
  filter_change_date: string | null;
  filter_start_date: string | null;
  filter_start_dp: number | null;
  // FS-580: the app card's Daily Change (last 30d trend), Pa/day. Null where
  // the card shows none (too few days, not reporting, calibrating).
  daily_change_pa: number | null;
  filter_thickness_inches: number | null;
  filter_is_new: boolean | null;
  filter_baseline_established: boolean | null;
  filter_baseline_source: string | null;
  filter_catalog_id: number | null;
  filter_catalog_label: string | null;
  // FS-443: per-device end-of-life and notification pressures (Pa). Null means
  // the device runs on the fleet default / legacy percentage rule.
  filter_eol_pa: number | null;
  filter_notify_pa: number | null;
  context_status: AppContextStatus | null;
  app_owner_uid: string | null;
  app_owner_name: string | null;
  app_owner_email: string | null;
  // FS-646 — true when the owner's address is confirmed the way the send
  // gate's verified tier counts it; null with no claim holder or no address.
  // Optional: an API before FS-646 omits it, and the column renders a dash.
  owner_email_verified?: boolean | null;
  app_location_id: string | null;
  app_location_name: string | null;
  app_hvac_id: string | null;
  app_hvac_name: string | null;
  app_hvac_manufacturer: string | null;
  app_hvac_model: string | null;
  app_hvac_tonnage: number | null;
  app_hvac_btu_rating: number | null;
  app_hvac_blower_type: string | null;
  app_ac_name: string | null;
  app_ac_manufacturer: string | null;
  app_ac_model: string | null;
  app_ac_seer: number | null;
  app_ac_compressor_stages: string | null;
  app_filter_summary: string | null;
  // Plan 119 Phase 1 (FS-618) — provenance for the spec values above.
  app_hvac_tonnage_source: string | null;
  app_hvac_tonnage_side: string | null;
  app_hvac_tonnage_confidence: number | null;
  app_hvac_tonnage_at: string | null;
  app_hvac_btu_rating_source: string | null;
  app_hvac_btu_rating_side: string | null;
  app_hvac_btu_rating_confidence: number | null;
  app_hvac_btu_rating_at: string | null;
  app_hvac_blower_type_source: string | null;
  app_hvac_blower_type_side: string | null;
  app_hvac_blower_type_confidence: number | null;
  app_hvac_blower_type_at: string | null;
  app_ac_seer_source: string | null;
  app_ac_seer_side: string | null;
  app_ac_seer_confidence: number | null;
  app_ac_seer_at: string | null;
  app_ac_compressor_stages_source: string | null;
  app_ac_compressor_stages_side: string | null;
  app_ac_compressor_stages_confidence: number | null;
  app_ac_compressor_stages_at: string | null;
  app_hvac_equipment_side: string | null;
  app_ac_equipment_side: string | null;
  app_hvac_capacity_source: string | null;
  app_ac_capacity_source: string | null;
  app_hvac_tonnage_capacity_source: string | null;
  app_hvac_photo_count: number | null;
  app_ac_photo_count: number | null;
  app_filter_photo_count: number | null;
  app_hvac_enrichment_version: number | null;
  // Plan 119 Phase 2 (FS-460 / FS-541) — the quality ladder, computed on the
  // server so these columns can be sorted and filtered. `*_rank` is 1 for the
  // best rung; `app_missing_fields` is a comma-joined list of the keys
  // (FS-752: tonnage, indoor_record, motor_type, filter_record — photos are no
  // longer listed), empty when nothing is missing.
  app_hvac_tonnage_quality: string | null;
  app_hvac_tonnage_quality_rank: number | null;
  app_hvac_blower_type_quality: string | null;
  app_hvac_blower_type_quality_rank: number | null;
  app_filter_quality: string | null;
  app_filter_quality_rank: number | null;
  // Plan 119 Phase 4 — the filter's own provenance and the catalog lineage.
  // `filter_catalog_match_source` is the source the match PINNED; the label
  // column beside it is built from the live catalog row.
  app_filter_model: string | null;
  app_filter_manufacturer_source: string | null;
  app_filter_manufacturer_at: string | null;
  app_filter_rating_source: string | null;
  app_filter_rating_at: string | null;
  app_filter_size_source: string | null;
  app_filter_size_at: string | null;
  app_filter_model_source: string | null;
  app_filter_model_at: string | null;
  app_filter_enrichment_version: number | null;
  filter_catalog_match_source: string | null;
  filter_catalog_confidence: string | null;
  filter_catalog_size_verified: boolean | null;
  filter_catalog_matched_at: string | null;
  filter_catalog_updated_at: string | null;
  app_missing_count: number | null;
  app_missing_fields: string | null;
  // FS-752 — the furnace or air handler record's quality code and its rank
  // (1 = best), who entered the indoor model number, and whether the current
  // filter's airflow record is calibrated (`calibrated` / `not_calibrated` /
  // `no_record`). Optional: an API before FS-752 omits them.
  app_indoor_record_quality?: string | null;
  app_indoor_record_quality_rank?: number | null;
  app_hvac_model_source?: string | null;
  app_airflow_calibration?: string | null;
  // Plan 123 Phase 1 — the equipment score: how many components of this
  // system still need information (0 when none do). Optional because an API
  // that predates the column omits the field entirely; the column renders an
  // empty cell for that, never a 0.
  app_equipment_score?: number | null;
  // FS-881 — open equipment reviews this Clip's edit drawer can act on: those
  // on its HVAC system bound to its serial, plus any with no binding. Null
  // when not computed (no sync since the column was added, or no system).
  // Optional because an API that predates the column omits the field.
  app_open_review_count?: number | null;
  // FS-593 — this Clip's own filter slot. Optional: an API before FS-593
  // omits them, and the columns then render a dash.
  /** `airReturns` | `furnace`, or null when the slot has no type. */
  app_filter_type?: string | null;
  /** The homeowner's name for the slot ("Hallway"), or null. */
  app_filter_location?: string | null;
  /** Return grilles recorded on the system; null when unknown or conflicting. */
  app_hvac_grille_count?: number | null;
  /** 1-based position of this Clip's slot among the return grilles; null when not a grille. */
  app_filter_slot_ordinal?: number | null;
  // Plan 119 Phase 3 (FS-627) — what the equipment model catalog resolved.
  // `app_hvac_rated_esp_pa` is the manufacturer's published operating point
  // converted to Pa, NOT a filter end-of-life threshold.
  // The catalog id is a bigint in Postgres, so /devices/search sends it as a
  // string ("3") while the single-device read can send a number. Typed as
  // both, because it arrives as both — never compared with ===.
  app_hvac_catalog_model_id: number | string | null;
  app_ac_catalog_model_id: number | string | null;
  app_hvac_catalog_manufacturer: string | null;
  app_hvac_catalog_equipment_type: string | null;
  app_ac_catalog_equipment_type: string | null;
  app_hvac_rated_esp_pa: number | null;
  app_hvac_rated_esp_original: string | null;
  app_hvac_rated_esp_kind: string | null;
  app_hvac_rated_esp_document_id: number | null;
  app_hvac_rated_esp_confidence: number | null;
  app_hvac_rated_esp_at: string | null;
  app_hvac_blower_type_document_id: number | null;
  app_hvac_blower_type_conflict: boolean | null;
  app_hvac_tonnage_document_id: number | null;
  // FS-835/FS-838 — which unit gave the tonnage and what disagrees with it.
  // Optional, so a row from an API before FS-835 still type-checks.
  app_hvac_tonnage_component?: string | null;
  app_hvac_tonnage_conflict?: boolean | null;
  app_hvac_tonnage_alt?: number | null;
  app_hvac_tonnage_alt_side?: string | null;
  app_hvac_tonnage_alt_source?: string | null;
  app_rated_static_quality: string | null;
  app_rated_static_quality_rank: number | null;
  // Plan 120 — the coil child on the Device List row. Null on every system
  // without one; `app_coil_catalog_model_id` is a bigint and arrives as a
  // string, like the two catalog ids above.
  app_coil_id: string | null;
  app_coil_model: string | null;
  app_coil_manufacturer: string | null;
  app_coil_tonnage: number | string | null;
  app_coil_tonnage_source: string | null;
  app_coil_tonnage_at: string | null;
  app_coil_tonnage_confidence: number | null;
  app_coil_equipment_side: string | null;
  app_coil_catalog_model_id: number | string | null;
  app_coil_photo_count: number | null;
  app_coil_count: number | null;
  app_hvac_unclassified_photo_count: number | null;
  app_hvac_component_review_reason: string | null;
  last_heard: string | null;
  days_since_heard: number | null;
  pressure_x46_min_7d: number | null;
  pressure_x46_max_7d: number | null;
  pressure_x47_min_7d: number | null;
  pressure_x47_max_7d: number | null;
  // All pressure values are Pa: each sensor reads absolute pressure
  // (~99,000 Pa ≈ barometric), so the x46/x47 aggregates are big numbers
  // while the differential columns are the duct ΔP directly (Plan 103).
  dp_min_7d: number | null;
  dp_max_7d: number | null;
  dp_current: number | null;
  readings_7d: number;
  // Plan 103 operations columns. Runtime hours are the clustering-derived
  // fan-on estimate summed from daily_calcs (NULL = no computed days, not 0).
  runtime_since_filter_h: number | null;
  runtime_per_day_30d_h: number | null;
  // FS-441. Highest daily return-side transition delta (Pa) over the shorter
  // of the last 30 days and the current filter's life; return_static_days is
  // how many days in that window produced a value (NULL max = none did).
  return_static_max_pa: number | null;
  return_static_days: number;
  open_alerts: OpenAlert[] | null;
  open_alert_count: number;
  // FS-501: tag names on the device, sorted. Always present (empty array when
  // untagged) — the server COALESCEs the aggregate so this is never null.
  tags: string[];
  app_context: DeviceSearchAppContext;
}

/**
 * FS-501: one entry in the device tag vocabulary. `device_count` is present on
 * the vocabulary listing (and scoped to what the caller can see) but omitted
 * on the per-device listing, where it would be meaningless.
 */
export interface DeviceTag {
  id: number;
  name: string;
  device_count?: number;
}

// One open alert on a device (alerts table; at most one open row per
// device+type). internal_only alerts are hidden from app users but shown
// (badged) on the internal Device List.
export interface OpenAlert {
  type: string;
  severity: string;
  internal_only: boolean;
}

export type AlertSeverity = 'info' | 'warning' | 'urgent';

// One row of the alerts table as the device-data-api admin REST list returns
// it (GET <alertsApiUrl>). "Open" is closed_at === null — a snoozed row is
// still open. internal_only marks an alert meant for internal use: normal
// emission suppresses mobile delivery, but the REST API returns the row to
// any caller with read_alerts in scope. FS-667: shared by the Alerts page
// and the Analysis tab's open-alerts box.
export interface Alert {
  id: string;
  device_id: number | null;
  device_name: string;
  user_id: number | null;
  entity_path: string;
  filter_path: string | null;
  type: string;
  severity: AlertSeverity;
  title: string;
  body: string;
  context: Record<string, unknown> | null;
  opened_at: string;
  closed_at: string | null;
  close_reason: string | null;
  snooze_until: string | null;
  fcm_sent_at: string | null;
  email_sent_at: string | null;
  internal_only: boolean;
}

// The list envelope. `total` counts every row matching the filters within the
// caller's scope, before limit/offset; rows are sorted opened_at DESC.
export interface AlertListResponse {
  total: number;
  limit: number;
  offset: number;
  rows: Alert[];
}

export interface DeviceSearchFacet {
  value: string;
  label?: string;
  count: number;
}

// Plan 94: GET /devices/:id/app-claims — full Firebase claim history for one
// device (the detail behind an ambiguous/stale flag).
export interface DeviceAppClaim {
  id: string;
  firestore_device_path: string;
  owner_uid: string | null;
  owner_name: string | null;
  owner_email: string | null;
  location_id: string | null;
  location_name: string | null;
  hvac_id: string | null;
  hvac_name: string | null;
  filter_summary: string | null;
  firestore_update_time: string | null;
  is_current: boolean;
}

export interface DeviceAppClaimsResponse {
  status: AppContextStatus;
  duplicate_claim_count: number;
  claims: DeviceAppClaim[];
}

// Plan 94: live Firestore claim inspection from firebase-api
// (GET /fb/app-claims/:serial) — authoritative truth used by the cleanup UI,
// with orphan + empty-branch signals the RDS projection snapshot can't give.
export interface AppClaimLive {
  path: string;
  uid: string | null;
  location_id: string | null;
  hvac_id: string | null;
  owner_name: string | null;
  owner_email: string | null;
  location_name: string | null;
  hvac_name: string | null;
  filter_summary: string | null;
  update_time: string | null;
  is_current: boolean;
  owner_exists: boolean;
  location_exists: boolean;
  hvac_exists: boolean;
  hvac_other_device_count: number;
  /** FS-914: an earlier registration shown to a caller without root scope: the
   *  owner, location, HVAC and filter details are null. Missing from older servers. */
  details_hidden?: boolean;
}

export interface AppClaimsLiveResponse {
  serial: string;
  status: AppContextStatus;
  claims: AppClaimLive[];
}

// Exactly what a delete would remove (from the dry_run preview). Never includes
// a user doc or Firebase Auth account.
export interface DeleteClaimsPlan {
  device_paths: string[];
  hvac_paths: string[];
  location_paths: string[];
}

// Plan 101: facets are option lists (value + count, label for id-keyed
// columns) for every enum/prefix filter column, keyed by facet name — the
// original five plus state, country, time_zone, firmware_version,
// bootloader_version, wifi_version, filter_baseline_source, filter_catalog.
export interface DeviceSearchResponse {
  devices: DeviceSearchRow[];
  facets: Record<string, DeviceSearchFacet[]>;
  total: number;
  limit: number;
  offset: number;
  /**
   * FS-501: how the server resolved tag filter ids, present only when it
   * changed something. `remapped` is old id -> the id it was merged into;
   * `gone` lists ids whose tag was deleted outright. Absent on the common
   * request, where every id is still live.
   */
  tag_filter_changes?: Record<
    string,
    { remapped: Record<string, string>; gone: string[] }
  > | null;
}

// Firmware Management Types (Plan 25)
// Mirrors firmware-manager Lambda's store.Image struct.
export interface FirmwareImage {
  id: number;
  version: number;
  device_type: string;
  s3_bucket: string;
  s3_key: string;
  original_filename?: string | null;
  size_bytes?: number | null;
  sha256_hash?: string | null;
  uploaded_by?: number | null;
  uploaded_at?: string | null;
  notes?: string | null;
  published_at?: string | null;
  published_by?: number | null;
  unpublished_at?: string | null;
  unpublished_by?: number | null;
  last_block_size?: number | null;
  last_block_count?: number | null;
  last_padded_size?: number | null;
  last_crc32?: string | null;
  publish_topic_prefix?: string | null;
}

// Derived status from a FirmwareImage's state fields.
// Mirrors the Go store.Image.Status() method.
export type FirmwareStatus = 'pending' | 'uploaded' | 'published' | 'unpublished';

export function firmwareStatus(img: FirmwareImage): FirmwareStatus {
  if (!img.sha256_hash) return 'pending';
  if (!img.published_at) return 'uploaded';
  if (img.unpublished_at) return 'unpublished';
  return 'published';
}

export interface FirmwareListResponse {
  images: FirmwareImage[] | null;
  count: number;
}

export interface FirmwareUploadURLResponse {
  id: number;
  presigned_url: string;
  s3_bucket: string;
  s3_key: string;
  method: string;
  content_type: string;
  expires_in_seconds: number;
}

export interface FirmwareDownloadURLResponse {
  id: number;
  presigned_url: string;
  expires_in: number;
}

export interface NoteUploadURLResponse {
  presigned_url: string;
  image_url: string;
  content_type: string;
  expires_in_seconds: number;
}

// Firmware publish options. blockSize is optional; deviceId is optional
// single-device targeting (Matt's 2026-04-11 addition). When deviceId is
// set the start record includes a "device_id" field and only that device
// will flash the update; the rest of the fleet stays on its current
// firmware. Empty/undefined deviceId = fleet-wide (current default).
export interface FirmwarePublishOptions {
  blockSize?: number;
  deviceId?: string;
}

export type AnalysisParameterValue = string | number | boolean | null | AnalysisParameterValue[] | { [key: string]: AnalysisParameterValue };
export type AnalysisParameters = Record<string, AnalysisParameterValue>;
export interface AnalysisParameter {
  key: string;
  default: number | boolean;
  kind: 'number' | 'integer' | 'boolean';
  minimum: number | null;
  exclusive_minimum: boolean;
  maximum: number | null;
  exclusive_maximum: boolean;
}
export interface AnalysisParameterContract {
  version: string;
  parameters: AnalysisParameter[];
  combination_rules: string[];
}
export interface AnalysisParameterDefaults {
  analysis_key: string;
  display_name: string;
  handler_key: string;
  status: string;
  contract: AnalysisParameterContract | null;
  definition_defaults: AnalysisParameters;
  effective_defaults: AnalysisParameters | null;
}
export interface AnalysisParameterContractsResponse {
  schema: 'analysis-parameter-contracts/1';
  methods: AnalysisParameterDefaults[];
}
export interface AnalysisRebuildStatus {
  status: 'none' | 'queued' | 'rebuilding' | 'completed' | 'failed' | 'dispatch_failed';
  requested_at: string | null;
  start_date: string | null;
  expected_days: number;
  completed_days: number;
  failed_dates: string[];
  latest_error: { date: string; error_message: string } | null;
  model_through: string | null;
}
export interface DeviceAnalysisParameters {
  analysis_key: string;
  display_name: string;
  handler_key: string;
  enabled: boolean;
  is_default: boolean;
  params_override: AnalysisParameters;
  definition_defaults: AnalysisParameters;
  contract: AnalysisParameterContract | null;
  effective_params: AnalysisParameters | null;
  model: { model_version: string; updated_through_date: string | null } | null;
  rebuild: AnalysisRebuildStatus;
  period: { start_date: string | null; replayable: boolean; reason: string | null };
}
export interface DeviceAnalysisParametersResponse {
  assignments: DeviceAnalysisParameters[];
  inherited: { analysis_key: string; display_name: string; handler_key: string;
    contract: AnalysisParameterContract | null; effective_params: AnalysisParameters | null } | null;
}
export interface SaveAnalysisParametersResponse {
  changed: boolean;
  params_override: AnalysisParameters;
  effective_params: AnalysisParameters;
  revision: string;
  rebuild: AnalysisRebuildStatus | null;
}
